import { AnalyticsBrowser } from '@segment/analytics-next';
import { withCookieYesConsent } from '@/util/analytics_consent';
import type { User } from '@/types/User';

const segmentApiKey = process.env.NEXT_PUBLIC_SEGMENT_API_KEY || '';

// Type cast is required because @segment/analytics-consent-tools' AnyAnalytics
// interface is a minimal stub that doesn't fully match AnalyticsBrowser's type.
// The runtime behaviour is correct — the wrapper supports AnalyticsBrowser.load() instances.
const analytics = withCookieYesConsent(
  AnalyticsBrowser.load({ writeKey: segmentApiKey }) as any
) as unknown as AnalyticsBrowser;

export default analytics;

/**
 * Source label for the "Réservé Interest Expressed" event, identifying which
 * surface the user clicked on.
 */
export type PriveeInterestSource =
  | 'account-page'
  | 'privee-page-logged-in'
  | 'privee-logged-in-express-interest-button';

/**
 * Track a logged-in user expressing interest in Réservé. Call after the
 * `isInterestedInPrivee` flag has successfully been persisted.
 *
 * `userId` is attached via the Segment options arg (not event properties) so
 * it doesn't pollute the event payload. PII (email, name) is intentionally
 * omitted — it's already on identify-traits set in UserContext, and
 * downstream destinations can hydrate via the userId join.
 */
export function trackPriveeInterestExpressed(
  user: Pick<User, 'id'> | null | undefined,
  source: PriveeInterestSource
) {
  if (!user) return;

  analytics.track(
    'Réservé Interest Expressed',
    { source },
    { userId: user.id }
  );
}

/**
 * Track a logged-in user joining the Réservé waitlist. Call after the
 * `priveeStatus = PENDING` flag has successfully been persisted.
 *
 * `userId` is attached via the Segment options arg (not event properties) so
 * it doesn't pollute the event payload. PII is intentionally omitted — it's
 * already on identify-traits set in UserContext.
 */
export function trackPriveeWaitlistJoined(
  user: Pick<User, 'id'> | null | undefined,
  selectVipInsurance = false
) {
  if (!user) return;

  analytics.track(
    'Réservé Waitlist Joined',
    { selectVipInsurance },
    { userId: user.id }
  );
}

/**
 * Track whether a member opted into VIP insurance at Réservé accept. Call on
 * submit before the accept-invitation request.
 */
export function trackVipInsuranceSelectedAtAccept(
  user: Pick<User, 'id'> | null | undefined,
  selected: boolean
) {
  if (!user) return;

  analytics.track(
    'VIP Insurance Selected At Accept',
    { selected },
    { userId: user.id }
  );
}

/** Track a Réservé member updating coverage on the manage-coverage page. */
export function trackVipInsuranceUpdated(
  user: Pick<User, 'id'> | null | undefined,
  fromSlug: string | undefined,
  toSlug: string
) {
  if (!user) return;

  analytics.track(
    'VIP Insurance Updated',
    { fromSlug, toSlug },
    { userId: user.id }
  );
}

/**
 * Track a view of a campaign PLP (e.g. the Summer Sale landing experience).
 * Fired once per landing from the client wrapper. Not user-gated — anonymous
 * visitors arriving from ads/email are attributed via Segment's anonymousId,
 * which is the bulk of campaign traffic.
 */
export function trackCampaignPLPViewed(campaignSlug: string) {
  analytics.track('Campaign PLP Viewed', { campaignSlug });
}
